<template>
  <div>
    <div v-if="showEmptyState" class="git-logs-empty text-center py-6 px-4">
      <p class="mb-1 p-3 gray--text text--darken-2">
        {{ $t("message.emptyTable.gitLogs.title") }}
      </p>
      <p class="mb-0 caption gray--text text--darken-1">
        {{ $t("message.emptyTable.gitLogs.description") }}
      </p>
    </div>

    <v-data-table
      v-else
      calculate-widths
      :headers="headers"
      :items="itemsLoading ? [] : items"
      :items-per-page="itemsPerPage"
      :page="page"
      hide-default-footer
      hide-default-header
      mobile-breakpoint="200"
      class="elevation-0 custom-data-table"
    >
    <template
      v-if="!$vuetify.breakpoint.mobile && !itemsLoading && items.length"
      v-slot:header="{ props }"
    >
      <tr class="custom-sort git-logs-header">
        <td
          v-for="header in props.headers"
          :key="header.value"
          class="sort-item"
          :class="header.value === 'date' ? 'text-end' : 'text-start'"
        >
          <div class="custom-sort__item">
            {{ $t(`table.header.${header.text}`) }}
          </div>
        </td>
      </tr>
    </template>

    <template v-if="itemsLoading" v-slot:body>
      <template v-if="$vuetify.breakpoint.lgAndDown">
        <logs-table-item-skeleton-mobile v-for="i in 5" :key="i" />
      </template>
      <template v-else>
        <logs-table-sort-skeleton />
        <logs-table-skeleton-item :cols="3" />
      </template>
    </template>

    <template v-slot:item.date="{ item }">
      <v-tooltip open-delay="150" close-delay="150" offset-y bottom>
        <template v-slot:activator="{ on, attrs }">
          <span
            v-bind="attrs"
            v-on="on"
            class="mb-0 gray--text text--darken-1 text-right d-block"
          >
            {{ moment(item.created_at).fromNow() }}
          </span>
        </template>
        <span>{{ moment(item.created_at).format("YYYY-MM-DD HH:mm") }}</span>
      </v-tooltip>
    </template>

    <template v-slot:item.action="{ item }">
      <div class="d-flex flex-column">
        <div class="text--gray-darken2 font-weight-medium">
          {{ actionLabel(item) }}
        </div>
      </div>
    </template>

    <template v-slot:item.commit="{ item }">
      <v-tooltip v-if="commitSummary(item)" open-delay="150" close-delay="150" offset-y bottom>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" class="caption gray--text text--darken-1 text-truncate commit-cell">
            <span v-if="item.commit_hash" class="font-weight-medium">{{ shortHash(item.commit_hash) }}</span>
            <span v-if="item.commit_message" :class="item.commit_hash ? 'ml-2' : ''">
              {{ item.commit_message }}
            </span>
          </div>
        </template>
        <span>{{ commitSummary(item) }}</span>
      </v-tooltip>
    </template>

    <template v-slot:item.status="{ item }">
      <v-tooltip
        v-if="statusErrorTooltip(item)"
        open-delay="150"
        close-delay="150"
        offset-y
        bottom
      >
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            v-bind="attrs"
            v-on="on"
            small
            outlined
            :color="statusColor(item.status)"
            class="font-weight-medium cursor-pointer"
          >
            {{ statusLabel(item) }}
          </v-chip>
        </template>
        <span>{{ statusErrorTooltip(item) }}</span>
      </v-tooltip>
      <v-chip
        v-else
        small
        outlined
        :color="statusColor(item.status)"
        class="font-weight-medium"
      >
        {{ statusLabel(item) }}
      </v-chip>
    </template>

    <template v-if="$vuetify.breakpoint.width < 1400" v-slot:item="{ item }">
      <tr>
        <td class="w-100">
          <div class="mobile-table-item">
            <div class="mobile-table-item__values">
              <div class="mobile-table-item__row">
                <div class="mobile-table-item__header">
                  {{ $t("table.header.action") }}
                </div>
                <div class="mobile-table-item__value">
                  <div class="d-flex flex-column">
                    <div class="text--gray-darken2 font-weight-medium">
                      {{ actionLabel(item) }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mobile-table-item__row">
                <div class="mobile-table-item__header">
                  {{ $t("table.header.commit") }}
                </div>
                <div class="mobile-table-item__value">
                  <div class="d-flex flex-column">
                    <v-tooltip v-if="commitSummary(item)" open-delay="150" close-delay="150" offset-y bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="caption gray--text text--darken-1 text-truncate commit-cell">
                          <span v-if="item.commit_hash" class="font-weight-medium">{{ shortHash(item.commit_hash) }}</span>
                          <span v-if="item.commit_message" :class="item.commit_hash ? 'ml-2' : ''">
                            {{ item.commit_message }}
                          </span>
                        </div>
                      </template>
                      <span>{{ commitSummary(item) }}</span>
                    </v-tooltip>
                  </div>
                </div>
              </div>

              <div class="mobile-table-item__row">
                <div class="mobile-table-item__header">
                  {{ $t("table.header.status") }}
                </div>
                <div class="mobile-table-item__value">
                  <v-tooltip
                    v-if="statusErrorTooltip(item)"
                    open-delay="150"
                    close-delay="150"
                    offset-y
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        outlined
                        :color="statusColor(item.status)"
                        class="font-weight-medium cursor-pointer"
                      >
                        {{ statusLabel(item) }}
                      </v-chip>
                    </template>
                    <span>{{ statusErrorTooltip(item) }}</span>
                  </v-tooltip>
                  <v-chip
                    v-else
                    small
                    outlined
                    :color="statusColor(item.status)"
                    class="font-weight-medium"
                  >
                    {{ statusLabel(item) }}
                  </v-chip>
                </div>
              </div>

              <div class="mobile-table-item__row">
                <div class="mobile-table-item__header">
                  {{ $t("table.header.date") }}
                </div>
                <div class="mobile-table-item__value">
                  <v-tooltip open-delay="150" close-delay="150" offset-y bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="mb-0 gray--text text--darken-1">
                        {{ moment(item.created_at).fromNow() }}
                      </span>
                    </template>
                    <span>{{ moment(item.created_at).format("YYYY-MM-DD HH:mm") }}</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </template>

    <template v-if="items.length" v-slot:footer>
      <data-iterator-footer
        :page="page"
        :numberOfPages="numberOfPages"
        :possibleItemsPerPage="possibleItemsPerPage"
        :itemsPerPage="itemsPerPage"
        :listStyle="listStyle"
        :itemsTotal="itemsTotal"
        :perPageLabel="perPageLabel"
        @update:page="$emit('update:page', $event)"
        @update:itemsPerPage="$emit('update:itemsPerPage', $event)"
      />
    </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from "moment";

import DataIteratorFooter from "../DataIteratorFooter.vue";
import LogsTableSkeletonItem from "./LogsTableSkeletonItem.vue";
import LogsTableItemSkeletonMobile from "./LogsTableItemSkeletonMobile.vue";
import LogsTableSortSkeleton from "./LogsTableSortSkeleton.vue";

export default {
  components: {
    DataIteratorFooter,
    LogsTableSkeletonItem,
    LogsTableItemSkeletonMobile,
    LogsTableSortSkeleton,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    itemsLoading: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    itemsPerPage: {
      type: Number,
      default: 20,
    },
    numberOfPages: {
      type: Number,
      default: 1,
    },
    possibleItemsPerPage: {
      type: Array,
      default: () => [[5, 15, 50, 100]],
    },
    listStyle: {
      type: Number,
      default: 0,
    },
    itemsTotal: {
      type: Number,
      default: 0,
    },
    perPageLabel: {
      type: String,
      default: "",
    },
  },
  data: function () {
    return {
      moment,
      headers: [
        { text: "action", value: "action", sortable: false, align: "start" },
        { text: "commit", value: "commit", sortable: false, align: "start" },
        { text: "status", value: "status", sortable: false, align: "start" },
        { text: "date", value: "date", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    showEmptyState: function () {
      return !this.itemsLoading && !this.items.length;
    },
  },
  methods: {
    actionLabel(item) {
      const action = (item?.action || "").toString().trim().toLowerCase();
      if (!action) {
        return "";
      }

      const logKey = `heading.instance.advanced.git.logs.action.${action}`;
      const logTranslated = this.$t(logKey);
      if (logTranslated !== logKey) {
        return logTranslated;
      }

      const titleKey = `heading.instance.advanced.git.${action}.title`;
      const titleTranslated = this.$t(titleKey);
      return titleTranslated === titleKey ? (item?.action || "") : titleTranslated;
    },
    statusLabel(item) {
      const status = (item?.status || "").toString().trim().toLowerCase();
      if (!status) {
        return "";
      }

      const key = `heading.instance.advanced.git.logs.status.${status}`;
      const translated = this.$t(key);
      return translated === key ? (item?.status || "") : translated;
    },
    shortHash(value) {
      const s = (value || "").toString();
      if (!s) return "";
      return s.length > 10 ? `${s.slice(0, 7)}…${s.slice(-3)}` : s;
    },
    isFailedStatus(status) {
      const v = (status || "").toString().trim().toLowerCase();
      return ["error", "failed", "failure", "skipped"].includes(v);
    },
    statusErrorTooltip(item) {
      if (!this.isFailedStatus(item?.status)) {
        return "";
      }

      const message = (item?.error_message || "").toString().trim();
      return message || "";
    },
    statusColor(value) {
      const v = (value || "").toString().toLowerCase();
      if (["success", "succeeded", "ok", "finished", "done"].includes(v)) return "success";
      if (["error", "failed", "failure"].includes(v)) return "error";
      if (["skipped"].includes(v)) return "warning";
      if (["pending", "in_progress", "running"].includes(v)) return "info";
      return "gray";
    },
    commitSummary(item) {
      const hash = (item?.commit_hash || "").toString().trim();
      const msg = (item?.commit_message || "").toString().trim();
      if (hash && msg) return `${hash} ${msg}`;
      return hash || msg || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.w-100 {
  width: 100%;
}

.commit-cell {
  max-width: 520px;
}

.custom-data-table::v-deep {
  table {
    width: 100%;
  }

  td {
    vertical-align: middle;
  }

  td.text-start {
    text-align: left !important;
  }

  td.text-end {
    text-align: right !important;
  }

  tbody td {
    text-align: left;
  }

  tbody td.text-end {
    text-align: right !important;
    white-space: nowrap;
  }
}
</style>

<style src="../TableCustomSort.scss" lang="scss" scoped />

